import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Star,
  StarOff,
  Search,
  Filter,
  Calendar,
  DollarSign,
  Clock,
  User
} from 'lucide-react';
import { API_URLS } from '../../../config/api';
import { adminApiFetch } from '../../../utils/adminApi';
import MediaSelector from '../MediaSelector';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    detailed_description: '',
    category_id: '',
    image_url: '',
    price: '',
    original_price: '',
    discount: 0,
    duration: '',
    rating: 0,
    reviews_count: 0,
    features: '',
    technologies: [],
    gallery: [],
    testimonials: [],
    process_steps: [],
    statistics: {},
    client_name: '',
    team_size: '',
    launch_date: '',
    project_year: new Date().getFullYear(),
    status: 'completed',
    project_url: '',
    demo_url: '',
    github_url: '',
    sort_order: 0,
    is_active: true,
    is_featured: false
  });

  useEffect(() => {
    fetchProjects();
    fetchCategories();
  }, []);

  const fetchProjects = async () => {
    try {
      console.log('Fetching projects...');
      setLoading(true);
      const response = await adminApiFetch(API_URLS.ADMIN.PROJECTS);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Non-JSON response: ${text.substring(0, 200)}...`);
      }
      
      const result = await response.json();
      console.log('Projects fetched:', result.data?.length || 0, 'projects');
      
      if (result.status === 'success') {
        setProjects(result.data || []);
        console.log('Projects state updated');
      } else {
        setProjects(result || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.PROJECT_CATEGORIES);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Non-JSON response: ${text.substring(0, 200)}...`);
      }
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setCategories(result.data || []);
      } else {
        setCategories(result || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingProject 
        ? API_URLS.ADMIN.UPDATE_PROJECT
        : API_URLS.ADMIN.CREATE_PROJECT;
      
      const method = editingProject ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(editingProject ? { id: editingProject.id } : {}),
          ...formData,
          // ensure features is array in backend
          features: Array.isArray(formData.features)
            ? formData.features
            : (formData.features || '').split(',').map(f => f.trim()).filter(Boolean),
          technologies: formData.technologies,
          gallery: formData.gallery,
          testimonials: formData.testimonials,
          process_steps: formData.process_steps,
          statistics: formData.statistics,
          team_size: formData.team_size ? Number(formData.team_size) : null,
          launch_date: formData.launch_date || null,
          project_url: formData.project_url || null,
          demo_url: formData.demo_url || null,
          github_url: formData.github_url || null
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        await fetchProjects();
        setShowModal(false);
        setEditingProject(null);
        setFormData({
          name: '',
          description: '',
          detailed_description: '',
          category_id: '',
          image_url: '',
          price: '',
          original_price: '',
          discount: 0,
          duration: '',
          rating: 0,
          reviews_count: 0,
          features: '',
          technologies: [],
          gallery: [],
          testimonials: [],
          process_steps: [],
          statistics: {},
          client_name: '',
          team_size: '',
          launch_date: '',
          project_year: new Date().getFullYear(),
          status: 'completed',
          project_url: '',
          demo_url: '',
          github_url: '',
          sort_order: 0,
          is_active: true,
          is_featured: false
        });
      } else {
        alert('Hata: ' + (result.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Hata: ' + error.message);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name || '',
      description: project.description || '',
      detailed_description: project.detailed_description || '',
      category_id: project.category_id || '',
      image_url: project.image_url || '',
      price: project.price || '',
      original_price: project.original_price || '',
      discount: project.discount || 0,
      duration: project.duration || '',
      rating: project.rating || 0,
      reviews_count: project.reviews_count || 0,
      features: Array.isArray(project.features) ? project.features.join(', ') : (project.features || ''),
      technologies: Array.isArray(project.technologies) ? project.technologies : [],
      gallery: Array.isArray(project.gallery) ? project.gallery : [],
      testimonials: Array.isArray(project.testimonials) ? project.testimonials : [],
      process_steps: Array.isArray(project.process_steps) ? project.process_steps : [],
      statistics: project.statistics || {},
      client_name: project.client_name || '',
      team_size: project.team_size || '',
      launch_date: project.launch_date || '',
      project_year: project.project_year || new Date().getFullYear(),
      status: project.status || 'completed',
      project_url: project.project_url || '',
      demo_url: project.demo_url || '',
      github_url: project.github_url || '',
      sort_order: project.sort_order || 0,
      is_active: project.is_active !== false,
      is_featured: project.is_featured === true
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu projeyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URLS.ADMIN.DELETE_PROJECT}?project_id=${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        await fetchProjects();
      } else {
        alert('Hata: ' + (result.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Hata: ' + error.message);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      console.log('Toggling project status for ID:', id);
      const response = await fetch(`${API_URLS.ADMIN.TOGGLE_PROJECT_STATUS}?project_id=${id}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Non-JSON response: ${text.substring(0, 200)}...`);
      }

      const result = await response.json();
      console.log('Toggle status response:', result);
      
      if (result.status === 'success') {
        console.log('Status toggled successfully, refreshing projects...');
        await fetchProjects();
        console.log('Projects refreshed');
      } else {
        alert('Hata: ' + (result.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Hata: ' + error.message);
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.TOGGLE_PROJECT_FEATURED}?project_id=${id}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Non-JSON response: ${text.substring(0, 200)}...`);
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        await fetchProjects();
      } else {
        alert('Hata: ' + (result.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
      alert('Hata: ' + error.message);
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || project.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Kategori Yok';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projeler</h1>
          <p className="text-gray-600">Projeleri yönetin</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Proje
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex gap-4 items-center">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Proje ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="w-48">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tüm Kategoriler</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map(project => (
          <div key={project.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="aspect-video bg-gray-100 relative">
              {project.image_url ? (
                <img 
                  src={project.image_url} 
                  alt={project.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-2 bg-gray-200 rounded"></div>
                    <p>Görsel Yok</p>
                  </div>
                </div>
              )}
              <div className="absolute top-2 right-2 flex gap-1">
                {project.is_featured && (
                  <span className="bg-yellow-500 text-white px-2 py-1 rounded text-xs">
                    <Star className="w-3 h-3 inline mr-1" />
                    Öne Çıkan
                  </span>
                )}
                {!project.is_active && (
                  <span className="bg-red-500 text-white px-2 py-1 rounded text-xs">
                    Pasif
                  </span>
                )}
              </div>
            </div>
            
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-gray-900">{project.name}</h3>
                <span className="text-sm text-gray-500">{getCategoryName(project.category_id)}</span>
              </div>
              
              <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                {project.description}
              </p>
              
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-green-600">
                    ₺{parseFloat(project.price || 0).toLocaleString()}
                  </span>
                  {project.original_price && project.original_price !== project.price && (
                    <span className="text-sm text-gray-500 line-through">
                      ₺{parseFloat(project.original_price).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <span className="text-sm text-gray-600">
                    {project.rating || 0} ({project.reviews_count || 0})
                  </span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(project)}
                  className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 flex items-center justify-center gap-1"
                >
                  <Edit className="w-3 h-3" />
                  Düzenle
                </button>
                <button
                  onClick={() => handleToggleStatus(project.id)}
                  className={`px-3 py-2 rounded text-sm flex items-center justify-center ${
                    project.is_active 
                      ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {project.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                </button>
                <button
                  onClick={() => handleToggleFeatured(project.id)}
                  className={`px-3 py-2 rounded text-sm flex items-center justify-center ${
                    project.is_featured 
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {project.is_featured ? <Star className="w-3 h-3" /> : <StarOff className="w-3 h-3" />}
                </button>
                <button
                  onClick={() => handleDelete(project.id)}
                  className="bg-red-100 text-red-700 px-3 py-2 rounded text-sm hover:bg-red-200 flex items-center justify-center"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Proje bulunamadı</h3>
          <p className="text-gray-600">
            {searchTerm || selectedCategory 
              ? 'Arama kriterlerinize uygun proje bulunamadı.' 
              : 'Henüz hiç proje eklenmemiş.'}
          </p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base sm:text-xl font-bold">
                {editingProject ? 'Proje Düzenle' : 'Yeni Proje'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingProject(null);
                  setFormData({
                    name: '',
                    description: '',
                    category_id: '',
                    image_url: '',
                    price: '',
                    original_price: '',
                    discount: 0,
                    duration: '',
                    rating: 0,
                    reviews_count: 0,
                    features: '',
                    client_name: '',
                    project_year: new Date().getFullYear(),
                    status: 'completed',
                    sort_order: 0,
                    is_active: true,
                    is_featured: false
                  });
                }}
                className="text-gray-400 hover:text-gray-600 text-lg sm:text-xl"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Proje Adı *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Kategori *
                  </label>
                  <select
                    required
                    value={formData.category_id}
                    onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Kategori Seçin</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Detaylı Açıklama
                </label>
                <textarea
                  value={formData.detailed_description}
                  onChange={(e) => setFormData({...formData, detailed_description: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Fiyat *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Orijinal Fiyat
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.original_price}
                    onChange={(e) => setFormData({...formData, original_price: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    İndirim (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discount}
                    onChange={(e) => setFormData({...formData, discount: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <MediaSelector
                  value={formData.image_url}
                  onChange={(value) => setFormData({...formData, image_url: value})}
                  label="Görsel"
                  placeholder="Proje görseli seçin..."
                />

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Süre
                  </label>
                  <input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    placeholder="6 hafta"
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Özellikler (virgülle ayırın)
                </label>
                <textarea
                  value={formData.features}
                  onChange={(e) => setFormData({...formData, features: e.target.value})}
                  rows={2}
                  placeholder="Özellik 1, Özellik 2, Özellik 3"
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Teknolojiler
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormData({
                      ...formData,
                      technologies: [...(formData.technologies || []), { name: '', icon: 'Code', category: '' }]
                    })}
                    className="text-blue-600 text-xs sm:text-sm hover:underline w-full sm:w-auto text-left sm:text-right"
                  >
                    + Ekle
                  </button>
                </div>
                <div className="space-y-3">
                  {(formData.technologies || []).map((tech, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3 items-center">
                      <input
                        type="text"
                        placeholder="Ad"
                        value={tech.name || ''}
                        onChange={(e)=>{
                          const arr=[...formData.technologies];
                          arr[idx] = { ...arr[idx], name: e.target.value };
                          setFormData({ ...formData, technologies: arr });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Icon (ör. Code, Server)"
                        value={tech.icon || 'Code'}
                        onChange={(e)=>{
                          const arr=[...formData.technologies];
                          arr[idx] = { ...arr[idx], icon: e.target.value };
                          setFormData({ ...formData, technologies: arr });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Kategori (opsiyonel)"
                          value={tech.category || ''}
                          onChange={(e)=>{
                            const arr=[...formData.technologies];
                            arr[idx] = { ...arr[idx], category: e.target.value };
                            setFormData({ ...formData, technologies: arr });
                          }}
                          className="flex-1 px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <button
                          type="button"
                          onClick={()=>{
                            const arr=[...formData.technologies];
                            arr.splice(idx,1);
                            setFormData({ ...formData, technologies: arr });
                          }}
                          className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 rounded-lg border border-red-200 whitespace-nowrap"
                        >Sil</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Galeri Görselleri
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, gallery: [...(formData.gallery || []), '' ] })}
                    className="text-blue-600 text-xs sm:text-sm hover:underline w-full sm:w-auto text-left sm:text-right"
                  >
                    + Ekle
                  </button>
                </div>
                <div className="space-y-3">
                  {(formData.gallery || []).map((url, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <input
                          type="url"
                          placeholder="https://.../image.jpg"
                          value={url}
                          onChange={(e)=>{
                            const arr=[...formData.gallery];
                            arr[idx] = e.target.value;
                            setFormData({ ...formData, gallery: arr });
                          }}
                          className="flex-1 px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <div className="flex items-center gap-2">
                          <MediaSelector
                            onSelect={(mediaUrl) => {
                              const arr = [...formData.gallery];
                              arr[idx] = mediaUrl;
                              setFormData({ ...formData, gallery: arr });
                            }}
                          />
                          <button
                            type="button"
                            onClick={()=>{
                              const arr=[...formData.gallery];
                              arr.splice(idx,1);
                              setFormData({ ...formData, gallery: arr });
                            }}
                            className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 rounded-lg border border-red-200 whitespace-nowrap"
                          >Sil</button>
                        </div>
                      </div>
                      {url && (
                        <div className="ml-4">
                          <img 
                            src={url} 
                            alt={`Galeri ${idx + 1}`} 
                            className="h-24 w-auto object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Yorumlar
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormData({
                      ...formData,
                      testimonials: [...(formData.testimonials || []), { id: `${Date.now()}-${Math.random()}`, name: '', position: '', content: '', rating: 5 }]
                    })}
                    className="text-blue-600 text-xs sm:text-sm hover:underline w-full sm:w-auto text-left sm:text-right"
                  >
                    + Ekle
                  </button>
                </div>
                <div className="space-y-3">
                  {(formData.testimonials || []).map((t, idx) => (
                    <div key={t.id || idx} className="grid grid-cols-1 md:grid-cols-5 gap-2 sm:gap-3 items-center">
                      <input
                        type="text"
                        placeholder="İsim"
                        value={t.name || ''}
                        onChange={(e)=>{
                          const arr=[...formData.testimonials];
                          arr[idx] = { ...arr[idx], name: e.target.value };
                          setFormData({ ...formData, testimonials: arr });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Pozisyon"
                        value={t.position || ''}
                        onChange={(e)=>{
                          const arr=[...formData.testimonials];
                          arr[idx] = { ...arr[idx], position: e.target.value };
                          setFormData({ ...formData, testimonials: arr });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Yorum"
                        value={t.content || ''}
                        onChange={(e)=>{
                          const arr=[...formData.testimonials];
                          arr[idx] = { ...arr[idx], content: e.target.value };
                          setFormData({ ...formData, testimonials: arr });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="number"
                        min="1"
                        max="5"
                        placeholder="Puan"
                        value={t.rating || 5}
                        onChange={(e)=>{
                          const arr=[...formData.testimonials];
                          arr[idx] = { ...arr[idx], rating: Number(e.target.value) };
                          setFormData({ ...formData, testimonials: arr });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={()=>{
                          const arr=[...formData.testimonials];
                          arr.splice(idx,1);
                          setFormData({ ...formData, testimonials: arr });
                        }}
                        className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 rounded-lg border border-red-200 whitespace-nowrap"
                      >Sil</button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Süreç Adımları
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormData({
                      ...formData,
                      process_steps: [...(formData.process_steps || []), { step: ((formData.process_steps?.length||0)+1), title: '', description: '', duration: '', icon: 'Target', completed: true }]
                    })}
                    className="text-blue-600 text-xs sm:text-sm hover:underline w-full sm:w-auto text-left sm:text-right"
                  >
                    + Ekle
                  </button>
                </div>
                <div className="space-y-3">
                  {(formData.process_steps || []).map((s, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-2 sm:gap-3 items-center">
                      <input
                        type="number"
                        min="1"
                        placeholder="Adım"
                        value={s.step || 1}
                        onChange={(e)=>{
                          const arr=[...formData.process_steps];
                          arr[idx] = { ...arr[idx], step: Number(e.target.value) };
                          setFormData({ ...formData, process_steps: arr });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Başlık"
                        value={s.title || ''}
                        onChange={(e)=>{
                          const arr=[...formData.process_steps];
                          arr[idx] = { ...arr[idx], title: e.target.value };
                          setFormData({ ...formData, process_steps: arr });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Açıklama"
                        value={s.description || ''}
                        onChange={(e)=>{
                          const arr=[...formData.process_steps];
                          arr[idx] = { ...arr[idx], description: e.target.value };
                          setFormData({ ...formData, process_steps: arr });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Süre"
                        value={s.duration || ''}
                        onChange={(e)=>{
                          const arr=[...formData.process_steps];
                          arr[idx] = { ...arr[idx], duration: e.target.value };
                          setFormData({ ...formData, process_steps: arr });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Icon"
                          value={s.icon || 'Target'}
                          onChange={(e)=>{
                            const arr=[...formData.process_steps];
                            arr[idx] = { ...arr[idx], icon: e.target.value };
                            setFormData({ ...formData, process_steps: arr });
                          }}
                          className="flex-1 px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <button
                          type="button"
                          onClick={()=>{
                            const arr=[...formData.process_steps];
                            arr.splice(idx,1);
                            setFormData({ ...formData, process_steps: arr });
                          }}
                          className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 rounded-lg border border-red-200 whitespace-nowrap"
                        >Sil</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    İstatistikler
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const stats = { ...(formData.statistics || {}) };
                      let key = 'metric';
                      let i = 1;
                      while (stats[`${key}${i}`]) i++;
                      stats[`${key}${i}`] = '';
                      setFormData({ ...formData, statistics: stats });
                    }}
                    className="text-blue-600 text-xs sm:text-sm hover:underline w-full sm:w-auto text-left sm:text-right"
                  >
                    + Ekle
                  </button>
                </div>
                <div className="space-y-3">
                  {Object.entries(formData.statistics || {}).map(([k, v], idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3 items-center">
                      <input
                        type="text"
                        value={k}
                        onChange={(e)=>{
                          const entries = Object.entries(formData.statistics || {});
                          entries[idx] = [e.target.value, entries[idx][1]];
                          const stats = {};
                          for (const [kk, vv] of entries) { stats[kk] = vv; }
                          setFormData({ ...formData, statistics: stats });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={v}
                        onChange={(e)=>{
                          const entries = Object.entries(formData.statistics || {});
                          entries[idx] = [entries[idx][0], e.target.value];
                          const stats = {};
                          for (const [kk, vv] of entries) { stats[kk] = vv; }
                          setFormData({ ...formData, statistics: stats });
                        }}
                        className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={()=>{
                          const stats = { ...(formData.statistics || {}) };
                          delete stats[k];
                          setFormData({ ...formData, statistics: stats });
                        }}
                        className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50 rounded-lg border border-red-200 whitespace-nowrap"
                      >Sil</button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Müşteri Adı
                  </label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Proje Yılı
                  </label>
                  <input
                    type="number"
                    min="2020"
                    max="2030"
                    value={formData.project_year}
                    onChange={(e) => setFormData({...formData, project_year: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Durum
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="completed">Tamamlandı</option>
                    <option value="in_progress">Devam Ediyor</option>
                    <option value="planned">Planlandı</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Ekip Boyutu
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.team_size}
                    onChange={(e) => setFormData({...formData, team_size: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Başlangıç Tarihi
                  </label>
                  <input
                    type="date"
                    value={formData.launch_date}
                    onChange={(e) => setFormData({...formData, launch_date: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Puan
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={formData.rating}
                    onChange={(e) => setFormData({...formData, rating: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Yorum Sayısı
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.reviews_count}
                    onChange={(e) => setFormData({...formData, reviews_count: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Proje URL
                  </label>
                  <input
                    type="url"
                    value={formData.project_url}
                    onChange={(e) => setFormData({...formData, project_url: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Demo URL
                  </label>
                  <input
                    type="url"
                    value={formData.demo_url}
                    onChange={(e) => setFormData({...formData, demo_url: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    GitHub URL
                  </label>
                  <input
                    type="url"
                    value={formData.github_url}
                    onChange={(e) => setFormData({...formData, github_url: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-3 sm:gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="mr-2 w-4 h-4 sm:w-5 sm:h-5"
                  />
                  <span className="text-xs sm:text-sm text-gray-700">Aktif</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                    className="mr-2 w-4 h-4 sm:w-5 sm:h-5"
                  />
                  <span className="text-xs sm:text-sm text-gray-700">Öne Çıkan</span>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProject(null);
                    setFormData({
                      name: '',
                      description: '',
                      category_id: '',
                      image_url: '',
                      price: '',
                      original_price: '',
                      discount: 0,
                      duration: '',
                      rating: 0,
                      reviews_count: 0,
                      features: '',
                      client_name: '',
                      project_year: new Date().getFullYear(),
                      status: 'completed',
                      sort_order: 0,
                      is_active: true,
                      is_featured: false
                    });
                  }}
                  className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingProject ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;
